import { useState } from "react";
import { ImageIcon } from "lucide-react";

type BrowserCardProps = {
  src: string;
  alt: string;
  url: string;
  caption: string;
};

const WIDTHS = [400, 800, 1200, 1600];
const SIZES = "(max-width: 640px) 92vw, (max-width: 1024px) 45vw, 400px";

const buildSrcSet = (src: string, ext: "webp" | "jpg") => {
  const stem = src.replace(/\.jpg$/, "");
  return WIDTHS.map((w) => `${stem}-${w}w.${ext} ${w}w`).join(", ");
};

const BrowserCard = ({ src, alt, url, caption }: BrowserCardProps) => {
  const [imgFailed, setImgFailed] = useState(false);

  return (
    <div className="group relative rounded-xl overflow-hidden border border-cream/10 bg-[hsl(220,5%,12%)] shadow-xl shadow-black/30 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-0.5 hover:border-sage/30 hover:shadow-2xl hover:shadow-black/40">
      <div className="bg-[hsl(220,5%,16%)] border-b border-cream/10 px-3 py-2.5 flex items-center gap-2.5">
        <div className="flex items-center gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-[hsl(5,70%,55%)]" />
          <div className="h-2.5 w-2.5 rounded-full bg-[hsl(38,80%,55%)]" />
          <div className="h-2.5 w-2.5 rounded-full bg-[hsl(130,60%,50%)]" />
        </div>
        <div className="flex-1 mx-2 bg-[hsl(220,5%,12%)] rounded-md px-3 py-1 flex items-center gap-2 min-w-0">
          <div className="h-2 w-2 rounded-full bg-sage-bright shrink-0 shadow-[0_0_6px_hsl(var(--sage-bright)/0.6)]" />
          <span className="text-[10px] text-cream/60 font-mono truncate">{url}</span>
        </div>
      </div>

      <div className="relative aspect-[16/10] bg-cream/5 overflow-hidden">
        {imgFailed ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[hsl(220,6%,11%)]">
            <ImageIcon className="h-7 w-7 text-cream/40" strokeWidth={1.5} />
            <span className="text-[11px] text-cream/40 font-mono uppercase tracking-wider">Preview coming soon</span>
          </div>
        ) : (
          <picture>
            <source type="image/webp" srcSet={buildSrcSet(src, "webp")} sizes={SIZES} />
            <source type="image/jpeg" srcSet={buildSrcSet(src, "jpg")} sizes={SIZES} />
            <img
              src={src}
              alt={alt}
              width={800}
              height={500}
              loading="lazy"
              decoding="async"
              onError={() => setImgFailed(true)}
              className="w-full h-full object-cover object-top"
            />
          </picture>
        )}
      </div>

      <div className="px-4 pt-4 pb-3.5 border-t border-cream/10">
        <p className="text-xs text-cream/70 leading-snug md:text-sm">{caption}</p>
      </div>
    </div>
  );
};

export default BrowserCard;
